(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) {
    root.DRGAdditionalOrder = Object.assign(root.DRGAdditionalOrder || {}, api);
    root.document?.dispatchEvent(new CustomEvent('drg:additional-order-helper-ready'));
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  const MINIMUM_CENTS = 999;
  // Het minimum geldt voor wat de klant aan boodschappen betaalt. Statiegeld
  // komt als losse cartregel van de Canteen-app (producttype "fee rule"): dat
  // is borg en telt niet mee. Korting gaat er wel af. Liquid in cart.liquid en
  // delivery-address.liquid past dezelfde regel toe.
  const DEPOSIT_PRODUCT_TYPE = 'fee rule';
  const SESSION_KEY = 'drg.additionalOrder.v1';
  const POSTCODE_SESSION_KEY = 'drg.deliveryPostcode.v1';
  const PROOF_ATTRIBUTES = Object.freeze({
    proof: '__drg_additional_order_proof',
    nonce: '__drg_cart_nonce',
    deliveryStopId: '__drg_delivery_stop_id',
    deliveryDate: '__drg_proof_delivery_date',
    postcode: '__drg_proof_postcode',
    regionId: '__drg_proof_region_id',
  });

  function readNumber(value) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function isDepositLine(item) {
    return String((item && item.product_type) || '').trim().toLowerCase() === DEPOSIT_PRODUCT_TYPE;
  }

  function depositAmount(cart) {
    const items = Array.isArray(cart && cart.items) ? cart.items : [];
    return items.reduce((sum, item) => (isDepositLine(item) ? sum + readNumber(item.final_line_price) : sum), 0);
  }

  // Het laagste van de boodschappen zelf en het bedrag na korting: statiegeld
  // en de retourkrat-korting vallen zo tegen elkaar weg, maar statiegeld kan
  // het bedrag nooit boven de boodschappen tillen.
  function qualifyingAmount(cart) {
    const goods = readNumber(cart && cart.items_subtotal_price) - depositAmount(cart);
    const payable = cart && cart.total_price != null ? readNumber(cart.total_price) : goods;
    return Math.max(0, Math.min(goods, payable));
  }

  function createNonce(cryptoObject) {
    const bytes = new Uint8Array(18);
    cryptoObject.getRandomValues(bytes);
    return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  }

  function normalizeOption(value) {
    if (!value || typeof value !== 'object') return null;
    const option = {
      deliveryStopId: String(value.delivery_stop_id || ''),
      deliveryDate: String(value.delivery_date || ''),
      addressLabel: String(value.address_label || ''),
      postcode: String(value.postcode || ''),
      regionId: String(value.region_id || ''),
      regionName: String(value.region_name || ''),
      proof: String(value.additional_order_proof || ''),
      expiresAt: String(value.proof_expires_at || value.expires_at || ''),
    };
    if (
      !option.deliveryStopId ||
      !/^\d{4}-\d{2}-\d{2}$/.test(option.deliveryDate) ||
      !option.postcode ||
      !option.regionId ||
      !option.proof ||
      !option.expiresAt
    ) return null;
    return option;
  }

  function normalizeResponse(payload) {
    const data = payload && payload.data && typeof payload.data === 'object' ? payload.data : payload || {};
    const options = Array.isArray(data.options) ? data.options.map(normalizeOption).filter(Boolean) : [];
    return {
      eligible: data.eligible === true && options.length > 0,
      reasonCode: String(data.reason_code || 'additional_order_not_verified'),
      options,
    };
  }

  function contextFromOption(option, request, now) {
    return {
      version: 1,
      proof: option.proof,
      nonce: request.nonce,
      deliveryStopId: option.deliveryStopId,
      deliveryDate: option.deliveryDate,
      addressLabel: option.addressLabel,
      postcode: option.postcode,
      regionId: option.regionId,
      regionName: option.regionName,
      cartAmountMinor: request.cartAmountMinor,
      currency: request.currency,
      expiresAt: option.expiresAt,
      storedAt: new Date(now || Date.now()).toISOString(),
    };
  }

  function isContextUsable(context, cart, now) {
    if (!context || context.version !== 1 || typeof context !== 'object') return false;
    if (
      !context.proof ||
      !context.nonce ||
      !context.deliveryStopId ||
      !context.deliveryDate ||
      !context.postcode ||
      !context.regionId
    ) return false;
    if (qualifyingAmount(cart) !== readNumber(context.cartAmountMinor)) return false;
    if (String((cart && cart.currency) || '') !== String(context.currency || '')) return false;
    const expiry = Date.parse(context.expiresAt);
    return Number.isFinite(expiry) && expiry > (now || Date.now()) + 5000;
  }

  function canCarryMinimumForDeliveryDate(context, deliveryDate) {
    if (!context || context.version !== 1) return false;
    const provenDate = String(context.deliveryDate || '');
    const selectedDate = String(deliveryDate || '');
    return /^\d{4}-\d{2}-\d{2}$/.test(provenDate) && selectedDate === provenDate;
  }

  function contextMatchesCart(context, cart) {
    if (!context || context.version !== 1 || typeof context !== 'object') return false;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(context.deliveryDate || ''))) return false;
    if (qualifyingAmount(cart) !== readNumber(context.cartAmountMinor)) return false;
    return String((cart && cart.currency) || '') === String(context.currency || '');
  }

  function preferredOption(options, deliveryDate) {
    const normalizedOptions = Array.isArray(options) ? options.filter(Boolean) : [];
    const preferredDate = String(deliveryDate || '');
    if (preferredDate) {
      return normalizedOptions.find((option) => option.deliveryDate === preferredDate) || null;
    }
    return normalizedOptions.length === 1 ? normalizedOptions[0] : null;
  }

  function deliveryDateState(context, deliveryDate) {
    return canCarryMinimumForDeliveryDate(context, deliveryDate) ? 'carries_minimum' : 'new_delivery';
  }

  function prioritizeAvailableDates(dates, preferredDate, limit) {
    const options = Array.isArray(dates) ? dates.filter((option) => option && option.delivery_date) : [];
    const maximum = Math.max(1, readNumber(limit) || 2);
    const preferred = String(preferredDate || '');
    if (!preferred) return options.slice(0, maximum);
    const matched = options.find((option) => String(option.delivery_date) === preferred);
    if (!matched) return options.slice(0, maximum);
    return [matched, ...options.filter((option) => option !== matched)].slice(0, maximum);
  }

  function normalizePostcode(value) {
    return String(value || '').trim().replace(/\s+/g, '').toUpperCase();
  }

  function readPostcodePreference(storage, cartToken) {
    const expectedToken = String(cartToken || '');
    if (!expectedToken) return '';
    try {
      const value = JSON.parse(storage.getItem(POSTCODE_SESSION_KEY) || 'null');
      if (!value || value.version !== 1 || String(value.cartToken || '') !== expectedToken) return '';
      return normalizePostcode(value.postcode);
    } catch (_error) {
      return '';
    }
  }

  function writePostcodePreference(storage, cartToken, postcode, now) {
    const normalizedToken = String(cartToken || '');
    const normalizedPostcode = normalizePostcode(postcode);
    if (!normalizedToken || normalizedPostcode.length < 4) return false;
    try {
      storage.setItem(POSTCODE_SESSION_KEY, JSON.stringify({
        version: 1,
        cartToken: normalizedToken,
        postcode: normalizedPostcode,
        storedAt: new Date(now || Date.now()).toISOString(),
      }));
      return true;
    } catch (_error) {
      return false;
    }
  }

  function initialPostcode(preference, proofPostcode, customerPostcode) {
    return normalizePostcode(preference)
      || normalizePostcode(proofPostcode)
      || normalizePostcode(customerPostcode);
  }

  function proofAttributes(context) {
    return {
      [PROOF_ATTRIBUTES.proof]: context ? context.proof : '',
      [PROOF_ATTRIBUTES.nonce]: context ? context.nonce : '',
      [PROOF_ATTRIBUTES.deliveryStopId]: context ? context.deliveryStopId : '',
      [PROOF_ATTRIBUTES.deliveryDate]: context ? context.deliveryDate : '',
      [PROOF_ATTRIBUTES.postcode]: context ? context.postcode : '',
      [PROOF_ATTRIBUTES.regionId]: context ? context.regionId : '',
    };
  }

  function readSession(storage) {
    try {
      return JSON.parse(storage.getItem(SESSION_KEY) || 'null');
    } catch (_error) {
      return null;
    }
  }

  function writeSession(storage, context) {
    try {
      if (context) storage.setItem(SESSION_KEY, JSON.stringify(context));
      else storage.removeItem(SESSION_KEY);
      return true;
    } catch (_error) {
      return false;
    }
  }

  function readBrowserSession(browserWindow) {
    try {
      return readSession(browserWindow.sessionStorage);
    } catch (_error) {
      return null;
    }
  }

  function writeBrowserSession(browserWindow, context) {
    try {
      return writeSession(browserWindow.sessionStorage, context);
    } catch (_error) {
      return false;
    }
  }

  // Only use for reads (including the read-only App Proxy POST). Cart writes
  // stay serialized separately: retrying a timed-out write can reorder proofs.
  async function fetchJSONWithRetry(url, options = {}, policy = {}) {
    const fetcher = policy.fetcher || fetch;
    const retryDelays = policy.retryDelays || [250, 750];
    const timeoutMs = policy.timeoutMs || 4000;
    const signal = options.signal;
    for (let attempt = 0; ; attempt += 1) {
      if (signal?.aborted) throw new DOMException('Request cancelled', 'AbortError');
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal?.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, timeoutMs);
      let result;
      let failure;
      try {
        const response = await fetcher(url, { ...options, signal: controller.signal });
        const payload = await response.json();
        result = { response, payload };
      } catch (error) {
        failure = error;
      } finally {
        clearTimeout(timeout);
        signal?.removeEventListener('abort', abort);
      }
      if (signal?.aborted) throw new DOMException('Request cancelled', 'AbortError');
      const retryable = failure || [408, 500, 502, 503, 504].includes(result.response.status)
        || (result.response.ok && result.payload?.data?.reason_code === 'projection_lagging');
      if (!retryable || attempt >= retryDelays.length) {
        if (failure) throw failure;
        return result;
      }
      await new Promise((resolve, reject) => {
        const cancel = () => {
          clearTimeout(timer);
          reject(new DOMException('Request cancelled', 'AbortError'));
        };
        const timer = setTimeout(() => {
          signal?.removeEventListener('abort', cancel);
          resolve();
        }, retryDelays[attempt]);
        signal?.addEventListener('abort', cancel, { once: true });
        if (signal?.aborted) cancel();
      });
    }
  }

  return {
    MINIMUM_CENTS,
    DEPOSIT_PRODUCT_TYPE,
    SESSION_KEY,
    POSTCODE_SESSION_KEY,
    PROOF_ATTRIBUTES,
    isDepositLine,
    depositAmount,
    qualifyingAmount,
    createNonce,
    normalizeOption,
    normalizeResponse,
    contextFromOption,
    isContextUsable,
    canCarryMinimumForDeliveryDate,
    contextMatchesCart,
    preferredOption,
    deliveryDateState,
    prioritizeAvailableDates,
    normalizePostcode,
    readPostcodePreference,
    writePostcodePreference,
    initialPostcode,
    proofAttributes,
    readSession,
    writeSession,
    readBrowserSession,
    writeBrowserSession,
    fetchJSONWithRetry,
  };
});
